/**
 * Shared utility functions
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { slugifySkillName } from '@wayai/core';
import * as readline from 'node:readline';
import { expected } from './expected.js';

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Prompt the user for free-text input.
 */
export function prompt(question: string): Promise<string> {
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer.trim());
    });
  });
}

/**
 * Prompt the user for yes/no confirmation. Returns true if they answer 'y'.
 */
export function confirm(question: string): Promise<boolean> {
  const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => {
    rl.question(`${question} [y/N]: `, (answer) => {
      rl.close();
      resolve(answer.trim().toLowerCase() === 'y');
    });
  });
}

/** Prompt for a secret, masking typed characters with `*`. */
export function promptSecret(question: string): Promise<string> {
  return new Promise((resolve) => {
    const rl = readline.createInterface({ input: process.stdin, output: process.stdout });

    // Mute output by replacing _writeToOutput
    const originalWrite = (rl as unknown as { _writeToOutput: (s: string) => void })._writeToOutput;
    let firstWrite = true;
    (rl as unknown as { _writeToOutput: (s: string) => void })._writeToOutput = function (s: string) {
      if (firstWrite) {
        // Let the prompt question through
        originalWrite.call(rl, s);
        firstWrite = false;
      } else {
        // Replace typed characters with *
        originalWrite.call(rl, '*'.repeat(s.replace(/[\r\n]/g, '').length));
      }
    };

    rl.question(question, (answer) => {
      // Restore and print newline
      (rl as unknown as { _writeToOutput: (s: string) => void })._writeToOutput = originalWrite;
      process.stdout.write('\n');
      rl.close();
      resolve(answer);
    });
  });
}

/** Read all of stdin as a single trimmed string (for piped input). */
export function readStdin(): Promise<string> {
  return new Promise((resolve, reject) => {
    let data = '';
    process.stdin.setEncoding('utf-8');
    process.stdin.on('data', (chunk) => { data += chunk; });
    process.stdin.on('end', () => resolve(data.trim()));
    process.stdin.on('error', reject);
  });
}

/**
 * Kebab/snake slug for hub folders + skill names — the canonical
 * `slugifySkillName` from @wayai/core. The CLI now bundles @wayai/core (tsup
 * `noExternal`), so this is a real runtime import, not a hand-kept copy.
 */
export const slugify = slugifySkillName;

/**
 * Resolve the hub YAML path in a folder: tries hub.yaml first, falls back to wayai.yaml.
 * Returns the full path if found, null otherwise.
 */
export function resolveHubYamlPath(hubFolder: string): string | null {
  for (const filename of ['hub.yaml', 'wayai.yaml']) {
    const yamlPath = path.join(hubFolder, filename);
    if (fs.existsSync(yamlPath)) return yamlPath;
  }
  return null;
}

/**
 * Parse a shorthand period string (e.g. "7d", "4w", "3m", "1y") into
 * { start, end, periodicity } for analytics queries.
 */
export function parsePeriod(period: string): { start: string; end: string; periodicity: string } {
  const match = period.match(/^(\d+)([hdwmy])$/);
  if (!match) {
    throw new Error(`Invalid period "${period}". Use format like 24h, 7d, 4w, 3m, 1y.`);
  }

  const amount = parseInt(match[1], 10);
  const unit = match[2];

  if (amount <= 0) {
    throw new Error(`Invalid period "${period}". Amount must be greater than 0.`);
  }

  const now = new Date();
  const start = new Date(now);

  switch (unit) {
    case 'h':
      start.setHours(start.getHours() - amount);
      break;
    case 'd':
      start.setDate(start.getDate() - amount);
      break;
    case 'w':
      start.setDate(start.getDate() - amount * 7);
      break;
    case 'm':
      start.setMonth(start.getMonth() - amount);
      break;
    case 'y':
      start.setFullYear(start.getFullYear() - amount);
      break;
  }

  const periodicityMap: Record<string, string> = {
    h: 'daily',
    d: 'daily',
    w: 'weekly',
    m: 'monthly',
    y: 'yearly',
  };

  return {
    start: start.toISOString(),
    end: now.toISOString(),
    periodicity: periodicityMap[unit],
  };
}

/**
 * Parse --from/--to date strings into ISO format.
 * Accepts YYYY-MM-DD or full ISO datetime. Appends time suffix if needed.
 */
export function parseDateArg(date: string, position: 'from' | 'to'): string {
  if (date.includes('T')) {
    if (!/^\d{4}-\d{2}-\d{2}T/.test(date) || isNaN(new Date(date).getTime())) {
      throw new Error(`Invalid date format "${date}". Use YYYY-MM-DD or full ISO datetime.`);
    }
    // Verify the date portion doesn't overflow (e.g., Feb 31 → Mar 3)
    const datePart = date.slice(0, 10);
    const [year, month, day] = datePart.split('-').map(Number);
    const parsed = new Date(Date.UTC(year, month - 1, day));
    if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
      throw new Error(`Invalid date "${date}". Not a valid calendar date.`);
    }
    return date;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error(`Invalid date format "${date}". Use YYYY-MM-DD (e.g., 2026-01-01).`);
  }
  // Verify the date components are valid (JS Date silently overflows, e.g., Feb 31 → Mar 3)
  const [year, month, day] = date.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
    throw new Error(`Invalid date "${date}". Not a valid calendar date.`);
  }
  return position === 'from' ? `${date}T00:00:00Z` : `${date}T23:59:59Z`;
}

/**
 * Resolve organization_id from: --org flag → the repo's org binding → single-org auto-select.
 * Returns { organizationId, organizationName } or exits with an error.
 */
export async function resolveOrganizationId(
  client: { organizations(): Promise<{ organizations: Array<{ id: string; name: string }> }> },
  orgIdFlag?: string,
): Promise<{ organizationId: string; organizationName?: string }> {
  if (orgIdFlag) {
    if (!UUID_RE.test(orgIdFlag)) {
      console.error('Invalid --org value. Expected a UUID.');
      process.exit(1);
    }
    return { organizationId: orgIdFlag };
  }

  // Try the repo's org binding (wayai-ws/wayai.yaml, or the deprecated root file)
  // Lazy import to avoid circular dependency
  // NOT `readRepoConfig`: absent falls through to the single-org auto-select
  // below, so a repo whose two config files disagree would silently create the
  // credential in whichever organization happens to be the only one.
  const { readRepoConfigUnlessBlocked } = await import('./repo-config.js');
  const repoConfig = readRepoConfigUnlessBlocked();
  if (repoConfig) {
    return { organizationId: repoConfig.organization_id, organizationName: repoConfig.organization_name };
  }

  // Fetch orgs and auto-select if single
  const { organizations } = await client.organizations();
  if (organizations.length === 0) {
    console.error('No organizations found. Create one in the WayAI platform first.');
    process.exit(1);
  }
  if (organizations.length === 1) {
    console.log(`Organization: ${organizations[0].name}`);
    return { organizationId: organizations[0].id, organizationName: organizations[0].name };
  }

  console.error('Multiple organizations found. Specify one with --org <uuid>:');
  for (const org of organizations) {
    console.error(`  ${org.id}  ${org.name}`);
  }
  process.exit(1);
}

export function parsePositiveIntFlag(name: string, raw: string | undefined): number {
  if (raw === undefined) {
    console.error(`${name} requires a value`);
    process.exit(1);
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
    console.error(`${name} must be a positive integer.`);
    process.exit(1);
  }
  return n;
}

/**
 * Subcommands that render their own richer `--help`/`-h` output (and never run a
 * side effect on it). These fall through to their handler so the global-help
 * interception in `main()` doesn't shadow their command-specific help:
 *   eval          → commands/eval.ts (explicit case '--help')
 *   admin         → commands/admin.ts (fall-through printHelp)
 *   report        → commands/report.ts (fall-through printReportHelp)
 *   conversations → commands/conversations.ts (early wantsHelp → printConversationsHelp);
 *                   without this, `conversations --help` hides the `observability` subcommand
 *   communities   → commands/communities.ts (early wantsHelp → its usage)
 *
 * Single source of truth — `index.ts` consumes this for dispatch and the tests
 * assert against it directly, so a member can't silently drift out of the real
 * set without a test catching it.
 */
export const OWN_HELP_COMMANDS: ReadonlySet<string> = new Set([
  'eval',
  'admin',
  'report',
  'conversations',
  'alerts',
  'communities',
]);

/**
 * Low-level predicate: true when `--help`/`-h` appears anywhere in args.
 *
 * The match is deliberately broad (and so fail-safe — printing help is
 * non-destructive). The one realistic command that takes a free-text value that
 * could *equal* `--help` is `report` (`report create --description "--help"`),
 * and that command owns its help handling so it's excluded from interception by
 * `shouldInterceptHelp` — its own parser sees the value verbatim. Used both to
 * gate that interception and to skip the update check on any help invocation.
 */
export function wantsHelp(args: string[]): boolean {
  return args.includes('--help') || args.includes('-h');
}

/**
 * Whether the top-level entry should short-circuit to the GLOBAL help BEFORE
 * dispatching `command`. This stops side-effecting subcommands (e.g.
 * `run-eval`, which would otherwise create + run a billable eval session) from
 * executing on a `--help`/`-h` request.
 *
 * Returns false — i.e. falls through to the command's own handler — for:
 * - the top-level `help` slot (handled by the dispatch switch itself),
 * - an absent command, and
 * - commands in `ownHelpCommands` (e.g. `eval`, `admin`, `report`), which
 *   render their own richer command-specific help; intercepting them here would
 *   downgrade that to the global help.
 */
export function shouldInterceptHelp(
  command: string | undefined,
  args: string[],
  ownHelpCommands: ReadonlySet<string>,
): boolean {
  if (!command || command === 'help') return false;
  if (ownHelpCommands.has(command)) return false;
  return wantsHelp(args);
}

/** Returns true if `latest` is a newer semver than `current` */
export function isNewerVersion(latest: string, current: string): boolean {
  const strip = (v: string) => v.replace(/[-+].*$/, '');
  const l = strip(latest).split('.').map(Number);
  const c = strip(current).split('.').map(Number);
  for (let i = 0; i < Math.max(l.length, c.length); i++) {
    const lv = l[i] ?? 0;
    const cv = c[i] ?? 0;
    if (lv > cv) return true;
    if (lv < cv) return false;
  }
  return false;
}

/**
 * Decide where a secret comes from, and refuse the two combinations that fail
 * SILENTLY.
 *
 * Lives beside `promptSecret`/`readStdin` because it is the decision every
 * caller of them has to make, and getting it wrong is invisible in both
 * directions:
 *
 *  - **stdin on a TTY** — `readStdin` resolves only on `end`, which a terminal
 *    emits at Ctrl-D, so the command hangs having printed nothing WHILE the
 *    terminal driver echoes the typed secret in cleartext to screen and
 *    scrollback.
 *  - **prompt on a non-TTY** — readline hits EOF, `rl.question`'s callback never
 *    fires, the promise never settles, and node drains its loop and exits 0. A
 *    CI job goes green having created nothing.
 *
 * Neither shows in an exit code, which is why they were found one at a time
 * across five review rounds on one PR. `interactiveHint` is supplied by the
 * CALLER: only it knows which flags it declares, and a shared message naming
 * one caller's flag sends the other's users to an option that does not exist.
 */
export function resolveSecretSource(source: {
  /**
   * REQUIRED keys, not optional ones. Three separate defects in one PR were a
   * field added here and left unwired at a call site — an optional key is
   * invisible when forgotten, and no test can see a caller nobody wrote.
   * `undefined` is still a legal VALUE (a caller with no prompt flag passes it);
   * what the compiler now refuses is leaving the key out.
   */
  stdin: boolean | undefined;
  prompt: boolean | undefined;
  /** The caller's own flag spellings — a shared message naming one caller's flag misleads the others. */
  stdinFlag: string;
  promptHint: string;
}): 'stdin' | 'prompt' {
  // Contradictory input is REFUSED, not resolved. A precedence rule ("prompt
  // wins") had to answer a question with no right answer, and every answer left
  // an edge: with a pipe available it discarded the readable secret, and without
  // one it named the flag the caller had already passed.
  if (source.stdin && source.prompt) {
    throw expected(`${source.stdinFlag} and ${source.promptHint} are mutually exclusive — pass one.`);
  }
  if (source.stdin) {
    if (process.stdin.isTTY) {
      throw expected(
        `${source.stdinFlag} needs piped input. Pipe it — ` +
          `printf %s "$SECRET" | wayai … ${source.stdinFlag} — or ${source.promptHint}.`,
      );
    }
    return 'stdin';
  }
  if (!process.stdin.isTTY) {
    throw expected(
      `A secret cannot be prompted for when stdin is not a terminal. Pipe it instead: ` +
        `printf %s "$SECRET" | wayai … ${source.stdinFlag}`,
    );
  }
  return 'prompt';
}
